import { useState, useRef, useEffect, useCallback } from 'react';

// ── Google Places Autocomplete (same loader as DealCalculator) ────────────────
let googlePlacesLoaded = false;
function loadGooglePlaces(cb) {
  if (window.google && window.google.maps && window.google.maps.places && window.google.maps.places.PlaceAutocompleteElement) {
    cb(); return;
  }
  if (googlePlacesLoaded) {
    const interval = setInterval(() => {
      if (window.google && window.google.maps && window.google.maps.places && window.google.maps.places.PlaceAutocompleteElement) {
        clearInterval(interval); cb();
      }
    }, 100);
    return;
  }
  googlePlacesLoaded = true;
  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_PLACES_KEY}&libraries=places&loading=async`;
  script.async = true;
  script.onload = () => {
    const interval = setInterval(() => {
      if (window.google && window.google.maps && window.google.maps.places && window.google.maps.places.PlaceAutocompleteElement) {
        clearInterval(interval); cb();
      }
    }, 100);
  };
  document.head.appendChild(script);
}

const AddressAutocomplete = ({ onPlaceSelected }) => {
  const containerRef = useRef(null);
  const elementRef = useRef(null);
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  onPlaceSelectedRef.current = onPlaceSelected;

  useEffect(() => {
    loadGooglePlaces(() => {
      if (!containerRef.current || elementRef.current) return;
      const placeAutocomplete = new window.google.maps.places.PlaceAutocompleteElement({
        types: ['address'],
        componentRestrictions: { country: 'us' },
      });
      placeAutocomplete.style.width = '100%';
      containerRef.current.appendChild(placeAutocomplete);
      elementRef.current = placeAutocomplete;

      const handleSelect = async (e) => {
        try {
          const prediction = e.placePrediction || (e.detail && e.detail.placePrediction);
          if (!prediction) return;
          const place = prediction.toPlace();
          await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] });
          const get = (type) => {
            const c = place.addressComponents && place.addressComponents.find(c => c.types.includes(type));
            return c ? c.longText : '';
          };
          const getShort = (type) => {
            const c = place.addressComponents && place.addressComponents.find(c => c.types.includes(type));
            return c ? c.shortText : '';
          };
          const streetNumber = get('street_number');
          const route = get('route');
          const streetAddress = streetNumber && route ? `${streetNumber} ${route}` : place.formattedAddress || '';
          onPlaceSelectedRef.current({
            address: place.formattedAddress || '',
            streetAddress,
            city: get('locality') || get('sublocality') || get('neighborhood'),
            state: getShort('administrative_area_level_1'),
            zip: get('postal_code'),
            county: get('administrative_area_level_2').replace(' County', ''),
          });
        } catch (err) {
          console.error('Error handling place select:', err);
        }
      };
      placeAutocomplete.addEventListener('gmp-placeselect', handleSelect);
      placeAutocomplete.addEventListener('gmp-select', handleSelect);
    });
  }, []);

  return <div style={{ position: 'relative' }}><div ref={containerRef} style={{ width: '100%' }} /></div>;
};

// ── Main Component ────────────────────────────────────────────────────────────
function AIResearch() {
  const [address, setAddress] = useState('');
  const [auctionType, setAuctionType] = useState('Tax Deed');
  const [openingBid, setOpeningBid] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handlePlaceSelected = useCallback((fields) => {
    setAddress(fields.address || '');
  }, []);

  const research = async () => {
    if (!address.trim()) {
      setError('Please enter a property address.');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);

    const prompt = `You are a tax deed and real estate investment analyst with web search capability. Use web search to look up current data for this property before responding.

Search for:
1. The property on Zillow, Redfin, or Realtor.com to get current value estimates, beds/baths/sqft
2. Recent comparable sales within 0.5 miles sold in the last 6 months
3. Any known liens, tax delinquency, or title issues
4. Neighborhood ratings and rental demand

Property Address: ${address}
Auction Type: ${auctionType}
Opening Bid: ${openingBid ? '$' + openingBid : 'Unknown'}

After searching, provide a detailed investment analysis.

Property Address: ${address}
Auction Type: ${auctionType}
Opening Bid: ${openingBid ? '$' + openingBid : 'Unknown'}

Please research and provide the following in JSON format only (no other text):
{
  "propertyDetails": {
    "address": "full address",
    "city": "city",
    "state": "state",
    "zip": "zip",
    "county": "county name",
    "propertyType": "SFR/Condo/Multi-Family/Vacant Lot/Commercial",
    "beds": number or null,
    "baths": number or null,
    "sqft": number or null,
    "yearBuilt": number or null,
    "lotSize": "lot size description"
  },
  "valuation": {
    "estimatedValue": number or null,
    "estimatedARV": number or null,
    "estimatedRent": number or null,
    "zillowEstimate": number or null,
    "pricePerSqft": number or null
  },
  "comparables": [
    {
      "address": "address",
      "soldPrice": number,
      "sqft": number,
      "soldDate": "date",
      "notes": "brief note"
    }
  ],
  "lienRisks": {
    "irsLienRisk": "Low/Medium/High",
    "hoaLienRisk": "Low/Medium/High",
    "environmentalRisk": "Low/Medium/High",
    "titleRisk": "Low/Medium/High",
    "notes": "explanation of risks found"
  },
  "neighborhoodAnalysis": {
    "rating": "A/B/C/D",
    "description": "brief neighborhood description",
    "rentalDemand": "Low/Medium/High",
    "appreciation": "Low/Medium/High"
  },
  "investmentAnalysis": {
    "recommendation": "Strong Buy/Buy/Pass/Strong Pass",
    "reasoning": "2-3 sentence explanation",
    "estimatedRehab": number or null,
    "exitStrategy": "Flip/Rent/Wholesale/Hold",
    "risks": ["risk 1", "risk 2", "risk 3"],
    "opportunities": ["opportunity 1", "opportunity 2"]
  },
  "sources": ["source 1", "source 2"]
}

Base your analysis on publicly available data, typical market conditions for this area, and tax deed investment principles. Be realistic and conservative in valuations.`;

    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_ANTHROPIC_API_KEY,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: 'claude-opus-4-5',
          max_tokens: 4000,
          tools: [
            {
              type: 'web_search_20250305',
              name: 'web_search',
            }
          ],
          messages: [{ role: 'user', content: prompt }],
        }),
      });

      const data = await response.json();
      if (data.error) { setError('API Error: ' + data.error.message); setLoading(false); return; }

      // With web search, the response may have multiple content blocks (text + tool_use).
      // We need to handle multi-turn: if stop_reason is 'tool_use', send tool results back.
      let finalText = '';

      if (data.stop_reason === 'tool_use') {
        // Build tool results from web search blocks
        const toolResults = data.content
          .filter(block => block.type === 'tool_use')
          .map(block => ({
            type: 'tool_result',
            tool_use_id: block.id,
            content: block.input ? JSON.stringify(block.input) : 'Search completed',
          }));

        // Send follow-up with tool results
        const followUp = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_ANTHROPIC_API_KEY,
            'anthropic-version': '2023-06-01',
            'anthropic-dangerous-direct-browser-access': 'true',
          },
          body: JSON.stringify({
            model: 'claude-opus-4-5',
            max_tokens: 4000,
            tools: [{ type: 'web_search_20250305', name: 'web_search' }],
            messages: [
              { role: 'user', content: prompt },
              { role: 'assistant', content: data.content },
              { role: 'user', content: toolResults },
            ],
          }),
        });
        const followData = await followUp.json();
        if (followData.error) { setError('API Error: ' + followData.error.message); setLoading(false); return; }
        finalText = followData.content.filter(b => b.type === 'text').map(b => b.text).join('');
      } else {
        finalText = data.content.filter(b => b.type === 'text').map(b => b.text).join('');
      }

      // Strip markdown fences and extract JSON
      let clean = finalText.replace(/```json|```/g, '').trim();
      const start = clean.indexOf('{');
      const end = clean.lastIndexOf('}');
      if (start === -1 || end === -1) {
        setError('Could not parse AI response. Please try again.');
        setLoading(false);
        return;
      }
      clean = clean.substring(start, end + 1);
      const parsed = JSON.parse(clean);
      setResult(parsed);
    } catch (err) {
      setError('Error parsing response — please try again. (' + err.message + ')');
    }
    setLoading(false);
  };

  const fmt = (n) => n ? '$' + Number(n).toLocaleString() : 'N/A';
  const riskColor = (r) => r === 'Low' ? '#2d6a4f' : r === 'Medium' ? '#c9a84c' : '#c0392b';
  const recColor = (r) => {
    if (r === 'Strong Buy') return '#2d6a4f';
    if (r === 'Buy') return '#c9a84c';
    if (r === 'Pass') return '#888';
    return '#c0392b';
  };

  return (
    <div>
      <h2 style={{ marginBottom: '6px', fontSize: '20px' }}>AI Property Research</h2>
      <p style={{ color: '#666', fontSize: '13px', marginBottom: '20px' }}>
        Enter a property address and AI will research comparable sales, estimated value, lien risks, and investment analysis.
      </p>

      <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '16px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)', marginBottom: '20px' }}>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ fontSize: '13px', color: '#555', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>
            Property Address
            <span style={{ marginLeft: '6px', fontSize: '11px', color: '#c9a84c', fontStyle: 'italic', fontWeight: 'normal' }}>
              — type to search with autocomplete
            </span>
          </label>
          <AddressAutocomplete onPlaceSelected={handlePlaceSelected} />
          {address && (
            <div style={{ fontSize: '12px', color: '#2d6a4f', marginTop: '4px', padding: '4px 8px', backgroundColor: '#f0fff4', borderRadius: '4px' }}>
              ✓ {address}
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
          <div>
            <label style={{ fontSize: '13px', color: '#555', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>Deal Type</label>
            <select value={auctionType} onChange={e => setAuctionType(e.target.value)}
              style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', backgroundColor: 'white' }}>
              <option>Tax Deed</option>
              <option>Tax Lien</option>
              <option>Fix and Flip</option>
              <option>Buy and Hold</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '13px', color: '#555', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>Opening Bid ($)</label>
            <input type="text" inputMode="decimal" value={openingBid} onChange={e => setOpeningBid(e.target.value)}
              placeholder="Optional"
              style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', boxSizing: 'border-box' }} />
          </div>
        </div>

        {error && (
          <div style={{ backgroundColor: '#fff0f0', border: '1px solid #ffcccc', borderRadius: '6px', padding: '10px', marginBottom: '12px', fontSize: '13px', color: '#c0392b' }}>
            {error}
          </div>
        )}

        <button onClick={research} disabled={loading} style={{
          width: '100%', padding: '12px', backgroundColor: loading ? '#888' : '#1a1a2e',
          color: 'white', border: 'none', borderRadius: '6px',
          fontSize: '14px', fontWeight: 'bold', cursor: loading ? 'not-allowed' : 'pointer'
        }}>
          {loading ? 'Searching web & analyzing... (30-60 seconds)' : 'Research This Property'}
        </button>
      </div>

      {result && (
        <div>
          <div style={{ backgroundColor: recColor(result.investmentAnalysis.recommendation), borderRadius: '8px', padding: '16px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ margin: 0, color: 'white', fontSize: '12px', opacity: 0.8 }}>AI Recommendation</p>
              <p style={{ margin: '4px 0 0', color: 'white', fontSize: '22px', fontWeight: 'bold' }}>{result.investmentAnalysis.recommendation}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: 0, color: 'white', fontSize: '12px', opacity: 0.8 }}>Exit Strategy</p>
              <p style={{ margin: '4px 0 0', color: 'white', fontSize: '16px', fontWeight: 'bold' }}>{result.investmentAnalysis.exitStrategy}</p>
            </div>
          </div>

          <p style={{ fontSize: '13px', color: '#555', marginBottom: '16px', padding: '12px', backgroundColor: '#f8f8f8', borderRadius: '6px' }}>
            {result.investmentAnalysis.reasoning}
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '16px' }}>
            {[
              { label: 'Estimated Value', value: fmt(result.valuation.estimatedValue) },
              { label: 'Estimated ARV', value: fmt(result.valuation.estimatedARV) },
              { label: 'Est. Monthly Rent', value: fmt(result.valuation.estimatedRent) },
              { label: 'Est. Rehab Cost', value: fmt(result.investmentAnalysis.estimatedRehab) },
            ].map((item, i) => (
              <div key={i} style={{ backgroundColor: 'white', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <p style={{ margin: 0, fontSize: '11px', color: '#888' }}>{item.label}</p>
                <p style={{ margin: '4px 0 0', fontSize: '18px', fontWeight: 'bold', color: '#1a1a2e' }}>{item.value}</p>
              </div>
            ))}
          </div>

          {result.comparables && result.comparables.length > 0 && (
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '16px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <h3 style={{ margin: '0 0 12px', fontSize: '15px', color: '#1a1a2e' }}>Comparable Sales</h3>
              {result.comparables.map((comp, i) => (
                <div key={i} style={{ padding: '10px 0', borderBottom: i < result.comparables.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#333' }}>{comp.address}</span>
                    <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#2d6a4f' }}>{fmt(comp.soldPrice)}</span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#888' }}>
                    {comp.sqft ? comp.sqft.toLocaleString() + ' sqft' : ''}{comp.sqft && comp.soldDate ? ' · ' : ''}{comp.soldDate} {comp.notes ? '· ' + comp.notes : ''}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '16px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '15px', color: '#1a1a2e' }}>Lien & Title Risks</h3>
            {[
              { label: 'IRS Lien Risk', value: result.lienRisks.irsLienRisk },
              { label: 'HOA Lien Risk', value: result.lienRisks.hoaLienRisk },
              { label: 'Environmental Risk', value: result.lienRisks.environmentalRisk },
              { label: 'Title Risk', value: result.lienRisks.titleRisk },
            ].map((risk, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ fontSize: '13px', color: '#555' }}>{risk.label}</span>
                <span style={{ fontSize: '12px', fontWeight: 'bold', color: riskColor(risk.value), backgroundColor: riskColor(risk.value) + '20', padding: '2px 10px', borderRadius: '10px' }}>{risk.value}</span>
              </div>
            ))}
            {result.lienRisks.notes && (
              <p style={{ fontSize: '12px', color: '#666', marginTop: '8px', marginBottom: 0 }}>{result.lienRisks.notes}</p>
            )}
          </div>

          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '16px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '15px', color: '#1a1a2e' }}>Neighborhood</h3>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
              {[
                { label: 'Grade', value: result.neighborhoodAnalysis.rating },
                { label: 'Rental Demand', value: result.neighborhoodAnalysis.rentalDemand },
                { label: 'Appreciation', value: result.neighborhoodAnalysis.appreciation },
              ].map((item, i) => (
                <div key={i} style={{ backgroundColor: '#f8f8f8', padding: '8px 12px', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', color: '#888' }}>{item.label}</div>
                  <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#1a1a2e' }}>{item.value}</div>
                </div>
              ))}
            </div>
            <p style={{ fontSize: '13px', color: '#555', margin: 0 }}>{result.neighborhoodAnalysis.description}</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div style={{ backgroundColor: '#f0fff4', borderRadius: '8px', padding: '14px' }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '13px', color: '#2d6a4f' }}>✓ Opportunities</h4>
              {result.investmentAnalysis.opportunities.map((o, i) => (
                <p key={i} style={{ margin: '0 0 4px', fontSize: '12px', color: '#333' }}>• {o}</p>
              ))}
            </div>
            <div style={{ backgroundColor: '#fff5f5', borderRadius: '8px', padding: '14px' }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '13px', color: '#c0392b' }}>⚠ Risks</h4>
              {result.investmentAnalysis.risks.map((r, i) => (
                <p key={i} style={{ margin: '0 0 4px', fontSize: '12px', color: '#333' }}>• {r}</p>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AIResearch;
